<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="6">
          <validation-observer ref="batchLoanTerminationRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx, .xls, .csv format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx or .csv format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx, .xls, .csv"
                  @change="onLoadXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>
        <b-col
          md="6"
        >
          <b-row>
            <b-col
              md="5"
            >
              <Button
                v-if="getUserPermission('REPAY_LOAN') && !errorMessages.length"
                :label="$t('Terminate Batch Loans')"
                size="md"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate()"
              />
            </b-col>
            <b-col
              md="7"
            >
              <Button
                class="ml-2"
                align="end"
                :label="$t('Download Loan Termination Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="batchLoanDisburse"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows && !errorMessages.length"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BFormFile, BTable,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import Button from '../../components/button/Button.vue'
import { batchLoanDisburseFormat, tableAllHeaders } from '../../../helpers/data'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BTable,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'loanAccountNumber' },
      ],
      sortOrders: {},
      loanTerminationFormat: batchLoanDisburseFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableAllHeaders,
      required,
      isDisabled: false,
      isLoading: false,
      showFormError: false,
      errorMessage: '',
      errorHeaders: [
        { key: 'accountNumber', sortable: true, label: 'Loan Account Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      batchLoanTermination: 'batch/batchLoanTermination',
    }),
    async handleCreate() {
      this.$refs.batchLoanTerminationRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchLoanTerminationAction, { text: this.$t('Are you sure you want to Terminate this Loans?') })
        }
      })
    },
    async batchLoanTerminationAction() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        if (this.parseExcelFile.length) {
          const formData = this.parseExcelFile.map(item => ({
            loanAccountNumber: item.loanAccountNumber ? item.loanAccountNumber.toString() : '',
          }))
          const response = await this.batchLoanTermination({ requestModel: formData, initiatedBy: this.userDetails.email })
          const { status } = response
          if (status === 200) {
            this.hasFileData = []
            this.parseExcelFile = []
            this.totalSheetRows = ''
            this.alertSuccess({ text: this.$t('Your request to terminate batch loans was submitted successfully') })
          }
        } else {
          this.alertError({ text: this.$t('File is empty') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const { errorMessage } = responseError
          this.alertError({ text: errorMessage })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onLoadXLSX(event) {
      this.errorMessages = []
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet)
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            loanAccountNumber: items['Loan Account Number'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Loan Termination Format?') })
    },
    downloadAction() {
      const data = XLSX.utils.json_to_sheet(this.loanTerminationFormat)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, data, 'data')
      XLSX.writeFile(wb, 'Udara360-Batch-Loan-Termination.xlsx')
      this.alertSuccess({ text: this.$t('Your request to download Batch Loan Termination Format was successful') })
    },
  },
}
</script>
